import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { shuffle, take, isEmpty, size } from 'lodash';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { DISABLED_CHAT_STATE } from '../../consts/chatState';
import { ChatMessage } from './ChatMessage';
import { ChatSuggestions, suggestions } from './ChatSuggestions';
import styles from './chatHistory.module.scss';

const NUM_VISIBLE_SUGGESTIONS = 3;

export const ChatHistory = ({ onSuggestionSubmit, disabled = false, isLoading = false }) => {
    const intl = useIntl();
    const chatEndRef = useRef();
    const { value: chatHistory } = useFormFieldValue({ field: 'chatHistory' });
    const [visibleSuggestions, setVisibleSuggestions] = useState(() =>
        take(shuffle(suggestions), NUM_VISIBLE_SUGGESTIONS)
    );
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [usedSuggestionIds, setUsedSuggestionIds] = useState(new Set());
    const messages = disabled ? DISABLED_CHAT_STATE : chatHistory;
    const shouldShowSuggestions = !disabled && !isEmpty(visibleSuggestions);

    const unusedSuggestions = useMemo(() => suggestions.filter(s => !usedSuggestionIds.has(s.id)), [usedSuggestionIds]);

    const handleSuggestionClick = useCallback(
        async suggestion => {
            if (isLoading) {
                return;
            }

            const translatedText = intl.formatMessage({ id: suggestion.id, defaultMessage: suggestion.defaultText });
            setUsedSuggestionIds(prev => new Set([...prev, suggestion.id]));
            await onSuggestionSubmit(translatedText);
        },
        [onSuggestionSubmit, intl, isLoading]
    );

    const shuffleSuggestions = useCallback(() => {
        const [first] = unusedSuggestions || [];
        const newSuggestions =
            size(unusedSuggestions) === 1 ? [first] : take(shuffle(unusedSuggestions), NUM_VISIBLE_SUGGESTIONS);
        setVisibleSuggestions(newSuggestions);
    }, [unusedSuggestions]);

    const toggleSuggestions = useCallback(() => {
        if (isEmpty(unusedSuggestions)) {
            return;
        }
        setShowSuggestions(prev => !prev);
        if (!showSuggestions && !isEmpty(unusedSuggestions)) {
            shuffleSuggestions();
        }
    }, [unusedSuggestions, showSuggestions, shuffleSuggestions]);

    useEffect(() => {
        if (isEmpty(unusedSuggestions)) {
            setShowSuggestions(false);
            setVisibleSuggestions([]);
        } else {
            shuffleSuggestions();
        }
    }, [shuffleSuggestions, unusedSuggestions]);

    // Auto-scroll to bottom when content changes
    useLayoutEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, showSuggestions, visibleSuggestions]);

    return isEmpty(messages) ? null : (
        <div className={styles['container']}>
            {messages.map((messageData, index) => (
                <ChatMessage key={`${messageData.sender}-${index}`} {...messageData} />
            ))}
            {shouldShowSuggestions && (
                <ChatSuggestions
                    isLoading={isLoading}
                    visibleSuggestions={visibleSuggestions}
                    showSuggestions={showSuggestions}
                    onSuggestionClick={handleSuggestionClick}
                    onToggleSuggestions={toggleSuggestions}
                />
            )}
            <div ref={chatEndRef} />
        </div>
    );
};
